import { useCallback, useEffect, useState } from "react";

/**
 * The same three-state theme as `apps/web` and the operator console.
 *
 * Same `quovy-theme` key, same `light | dark | system` values, resolved onto
 * the same `dark` class on `<html>`. The portal is a different origin, so the
 * stored value is not shared with those surfaces.
 *
 * The initial class is applied by the inline script in `index.html`. This hook
 * adopts whatever that decided rather than recomputing it.
 */

export type Theme = "light" | "dark" | "system";

const STORAGE_KEY = "quovy-theme";
const ORDER: Theme[] = ["system", "light", "dark"];

function readStored(): Theme {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark" || stored === "system") return stored;
  } catch {
    /* Private mode can throw. Keep the portal's system default. */
  }
  return "system";
}

function apply(theme: Theme) {
  const dark =
    theme === "dark" ||
    (theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);
  document.documentElement.classList.toggle("dark", dark);
  document.documentElement.setAttribute("data-theme", theme);
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(readStored);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    apply(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* Preference is lost on reload; the page still renders correctly now. */
    }
  }, []);

  useEffect(() => {
    if (theme !== "system") return;
    const query = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => apply("system");
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, [theme]);

  const cycle = useCallback(() => {
    setTheme(ORDER[(ORDER.indexOf(theme) + 1) % ORDER.length]!);
  }, [theme, setTheme]);

  return { theme, setTheme, cycle };
}
